import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { useProjectStore } from '../project/project-store';
import type { Obligation, ObligationValueType } from '../types/api';

export const obligationsKeys = {
  all: ['obligations'] as const,
  list: (projectId: string | null, filters: ListFilters) =>
    [...obligationsKeys.all, 'list', projectId, filters] as const,
  byId: (projectId: string | null, id: string) =>
    [...obligationsKeys.all, 'byId', projectId, id] as const,
};

export interface ListFilters {
  q?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export function useObligationsList(filters: ListFilters = {}) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: obligationsKeys.list(projectId, filters),
    enabled: !!projectId,
    queryFn: async () => {
      const res = await api.get<{ obligations: Obligation[] }>('/obligations', { ...filters });
      return res.obligations;
    },
  });
}

export function useObligation(id: string | null) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: id ? obligationsKeys.byId(projectId, id) : ['obligations', 'none'],
    enabled: !!id && !!projectId,
    queryFn: async () => {
      const res = await api.get<{ obligation: Obligation }>(`/obligations/${id}`);
      return res.obligation;
    },
  });
}

// NO reviewer_user_id — obligations are configuration, not reviewed title
// work-product (spec §2.1). Mirrors api/src/routes/obligations.ts's
// createSchema/updateSchema baseObject exactly.
export interface ObligationInput {
  name: string;

  applies_to_lease?: boolean | undefined;
  applies_to_contract?: boolean | undefined;
  applies_to_well?: boolean | undefined;

  value_type?: ObligationValueType | undefined;

  attention?: string | null | undefined;
  address_1?: string | null | undefined;
  address_2?: string | null | undefined;
  city?: string | null | undefined;
  state?: string | null | undefined;
  zip?: string | null | undefined;
  phone?: string | null | undefined;
  fax?: string | null | undefined;
  email?: string | null | undefined;

  remarks?: string | null | undefined;
}

export function useCreateObligation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ObligationInput) =>
      api.post<{ obligation: Obligation }>('/obligations', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: obligationsKeys.all }),
  });
}

export function useUpdateObligation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<ObligationInput> }) =>
      api.patch<{ obligation: Obligation }>(`/obligations/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: obligationsKeys.all }),
  });
}

export function useDeleteObligation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/obligations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: obligationsKeys.all }),
  });
}
